import React, { useState } from 'react'
import ls from '@livesession/browser'
import ModalRecommendsSignUp from './ModalRecommendsSignUp.jsx'

import { accesslog } from '../../helpers/global.js'

const JUSTIFICATION_VISIBLE_SENTENCE_COUNT = 1
const JUSTIFICATION_VISIBLE_MAX_LENGTH = 140

// Returns the first sentence of a justification, hard-capped in length so a
// single very long sentence (no punctuation) can't push the "See more" link off-screen.
function getJustificationPreview(justification) {
  const sentences = justification.trim().match(/[^.!?]+[.!?]+(\s+|$)/g) || [justification]
  const visible = sentences.slice(0, JUSTIFICATION_VISIBLE_SENTENCE_COUNT).join('').trim()
  if (visible.length <= JUSTIFICATION_VISIBLE_MAX_LENGTH) {
    return visible
  }
  return `${visible.slice(0, JUSTIFICATION_VISIBLE_MAX_LENGTH).trim()}…`
}

// Strong -> accept (green), Adequate -> consider (orange), Weak -> reject (red),
// reusing the existing .recommends-pill tier classes rather than adding a new variant.
function getRubricTierClass(tier) {
  if (tier === 'Strong') {
    return 'accept'
  }
  if (tier === 'Adequate') {
    return 'consider'
  }
  return 'reject'
}

function getTierPoints(tier) {
  if (tier === 'Strong') {
    return 1
  }
  if (tier === 'Adequate') {
    return 0.5
  }
  return 0
}

// The BE rubric has no numeric score (M1-703 scoreTrialAnswer only returns per-criterion
// Strong/Adequate/Weak tiers), so we derive one client-side: average tier value across all
// rubric rows, scaled to a 10-point total. Averaging (rather than a fixed per-row weight)
// keeps this correct if the rubric's criterion count ever changes.
function computeScoreOutOfTen(rubric) {
  if (!rubric || rubric.length === 0) {
    return 0
  }
  const average = rubric.reduce((sum, row) => sum + getTierPoints(row.tier), 0) / rubric.length
  return Math.round(average * 10)
}

// Same strong/medium/weak buckets AstrntRecommends.jsx's getMatchTierClass uses for its
// (0-100) match_score badge, scaled down to this rubric-derived 0-10 score.
function getScoreTierClass(score) {
  if (score >= 8) {
    return 'strong'
  }
  if (score >= 5) {
    return 'medium'
  }
  return 'weak'
}

// Highest-tier row, shown unblurred as a taste of the result to entice sign-up.
function getBestRubricRow(rubric) {
  const tierPriority = ['Strong', 'Adequate', 'Weak']
  for (const tier of tierPriority) {
    const match = rubric.find((row) => row.tier === tier)
    if (match) {
      return match
    }
  }
  return rubric[0]
}

// Single row shown blurred under the lock -- most recent criterion, unless that's
// already the one shown unblurred as the "best" row above, in which case fall back
// to any other row so the same criterion isn't shown twice.
function getLockedRubricRow(rubric, bestRow) {
  const latest = rubric[rubric.length - 1]
  if (latest !== bestRow) {
    return latest
  }
  return rubric.find((row) => row !== bestRow) || latest
}

// Locked scoring-rubric preview shown after TryQuestionsWidget's flow produces a result,
// plus the "See Full Results" sign-up gate. Kept separate from TryQuestionsWidget because
// the two host pages lay the question flow and this preview out differently (a dedicated
// full-width section on /talent-acquisition/try-questions, inline in the homepage's
// compact "Try It Yourself" section). The sign-up modal always mounts (even pre-result) so
// the ?signup dev/QA shortcut can still open it directly; `wrapInSection` owns the
// full-width section markup for the standalone page and is turned off for the inline
// homepage placement.
const TryQuestionsResult = ({ assessResult, jobTitle, skill, questionText, questionCode, answerText, source, compact, openOnMount, wrapInSection }) => {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(!!openOnMount)

  const openSignUpModal = (trigger) => {
    accesslog(source, 'button_see_full_results')
    ls.track('signup_modal_shown', { trigger })
    setIsSignUpModalOpen(true)
  }

  const closeSignUpModal = () => {
    setIsSignUpModalOpen(false)
  }

  const bestRubricRow = assessResult && getBestRubricRow(assessResult.rubric)
  const lockedRubricRow = assessResult && getLockedRubricRow(assessResult.rubric, bestRubricRow)
  const score = assessResult && computeScoreOutOfTen(assessResult.rubric)

  const rubric = assessResult && (
    <div className={`recommends-results ${compact ? 'recommends-results-compact' : ''}`}>
      <div className="recommends-card recommends-card-assess">
        <div className={`recommends-score-badge recommends-score-badge-margin ${getScoreTierClass(score)}`}>
          <span className="recommends-score-badge-value">{score}</span>
          <span className="recommends-score-badge-max">/10</span>
        </div>

        <p className="recommends-assess-teaser">{getJustificationPreview(assessResult.justification)}</p>

        <div className="recommends-rubric-row recommends-rubric-best">
          <span className="recommends-rubric-criterion">{bestRubricRow.criterion}</span>
          <span className={`recommends-pill ${getRubricTierClass(bestRubricRow.tier)}`}>{bestRubricRow.tier}</span>
        </div>

        <div className="recommends-rubric-locked">
          <div className="recommends-rubric-row">
            <span className="recommends-rubric-criterion">{lockedRubricRow.criterion}</span>
            <span className={`recommends-pill ${getRubricTierClass(lockedRubricRow.tier)}`}>{lockedRubricRow.tier}</span>
            <p className="recommends-justification">{lockedRubricRow.description}</p>
          </div>

          <div className="recommends-see-more-overlay">
            <div className="recommends-see-more">
              <button
                type="button"
                className="recommends-see-more-btn"
                data-ls-id="assess-see-full-results-button"
                onClick={() => openSignUpModal('assess_see_full_results_click')}
              >
                <i className="fas fa-lock" /> See Full Breakdown
              </button>
              <p>Unlock the complete rubric breakdown and full justification.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <React.Fragment>
      {rubric && wrapInSection && (
        <div className="recommends-results-section" style={{ position: 'relative' }}>
          <section className="recommends-results-wrap">
            {rubric}
          </section>
        </div>
      )}
      {rubric && !wrapInSection && rubric}

      <ModalRecommendsSignUp
        isOpen={isSignUpModalOpen}
        onRequestClose={closeSignUpModal}
        jobTitle={jobTitle}
        skill={skill}
        questionText={questionText}
        questionCode={questionCode}
        answerText={answerText}
        assessResult={assessResult}
        signUpSource="try_question"
        title="You're one step away from your full results."
        subtext="Fill in a few quick details to unlock your full assessment breakdown."
        ctaLabel="Unlock Full Results →"
      />
    </React.Fragment>
  )
}

TryQuestionsResult.defaultProps = {
  source: 'try-questions',
  compact: false,
  openOnMount: false,
  wrapInSection: true
}

export default TryQuestionsResult
